import { Icon, type IconName } from '../ui/Icon'

/**
 * THE RAIL'S BOTTOM BAR.
 *
 * Owner, 2026-09-20, with a screenshot of Slack's: 「把 Side Panel 下面那個 Menu Bar
 * 改成這樣子的型態,下面塞五個 element,最右邊是一個小圓框的搜尋按鈕,搜尋按鈕上方就是
 * plus…然後在這上面加上一個 more 的按鈕」.
 *
 * So the bar is two shapes, not one: a PILL of destinations, and a round SEARCH
 * button beside it. Search left the pill because it is not a place — it is a
 * verb, and sitting it among the destinations made the rail's own list feel
 * like a fifth section of the app. The ＋ (App's #sbNewFab) floats above that
 * circle; it is anchored to the top of the dock, so revealing the search field
 * lifts both without a second measurement.
 *
 * `more` is a PANEL, not a tab — the same shape as `activity`. What it holds is
 * everything that is not agents: Artifacts today, and whatever the rail would
 * otherwise have to grow a section for. The owner's reason is the one worth
 * keeping: 「這樣子整個使用的型態就會更簡潔,不會讓所有的東西都跟 Agents 混在一起」.
 */
export type NavTab = 'home' | 'group' | 'activity' | 'more' | 'search'

/** The destinations, in the pill. Search is not among them — see above. */
const TABS: { id: NavTab; label: string; icon: IconName }[] = [
  { id: 'home', label: 'Agents', icon: 'smart_toy' },
  { id: 'group', label: 'Humans', icon: 'group' },
  { id: 'activity', label: 'Activity', icon: 'notifications' },
  { id: 'more', label: 'More', icon: 'more_horiz' },
]

export function NavBar({
  tab,
  onPick,
  unread,
}: {
  tab: NavTab
  onPick: (t: NavTab) => void
  unread: Partial<Record<NavTab, number>>
}) {
  return (
    <div id="sbNavRow">
      <nav id="sbNav" aria-label="Sections">
        {TABS.map((t) => {
          const on = tab === t.id
          const n = unread[t.id] ?? 0
          return (
            <button
              key={t.id}
              type="button"
              className={`sbnav-item${on ? ' on' : ''}`}
              aria-current={on ? 'page' : undefined}
              aria-label={n > 0 ? `${t.label}, ${n} unread` : t.label}
              onClick={() => onPick(t.id)}
            >
              <Icon name={t.icon} size={19} />
              <span className="sbnav-label">{t.label}</span>
              {n > 0 && (
                /* The count rides the ICON, not the label: on the label it would
                   shift the text and knock the other tabs out of alignment every
                   time a busy morning happened. */
                <span className="sbnav-badge">{n > 99 ? '99+' : n}</span>
              )}
            </button>
          )
        })}
      </nav>

      {/* Its own circle, to the right, under the ＋ — the arrangement in the
          owner's screenshot. Still a NavTab so App's one `pickNav` keeps
          deciding what opening search means. */}
      <button
        type="button"
        id="sbSearchBtn"
        className={tab === 'search' ? 'on' : undefined}
        aria-current={tab === 'search' ? 'page' : undefined}
        aria-label="Search"
        title="Search"
        onClick={() => onPick('search')}
      >
        <Icon name="search" size={19} />
      </button>
    </div>
  )
}
